import type { ListBuilderConfiguration } from '../types';

/**
 * Opportunity Notes list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const OPPORTUNITY_NOTES_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-opportunity-notes',
			cacheTTL: 600000,
			name: 'All Opportunity Notes',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'pin-toggle',
					fields: [
						{
							id: 'pin-toggle',
							displayStyle: 'pin_toggle',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'isPinned',
									objectTable: 'opportunity_note',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
						},
					],
					headers: [
						{
							id: 'h-pin',
							text: '',
						},
					],
					width: 50,
					sortable: false,
				},
				{
					id: 'note-content',
					fields: [
						{
							id: 'content-field',
							displayStyle: 'text',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'content',
									objectTable: 'opportunity_note',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
								textExpansion: {
									enabled: true,
									maxLines: 2,
								},
							},
						},
					],
					headers: [
						{
							id: 'h-content',
							text: 'Note',
						},
					],
					sortable: true,
					widthConfig: {
						mode: 'flexible',
						preset: 'xlarge',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'creator-column',
					fields: [
						{
							id: 'creator-field',
							displayStyle: 'text',
							dataConfig: {
								type: 'template',
								template: '{creatorFirstName} {creatorLastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
						},
					],
					headers: [
						{
							id: 'h-creator',
							text: 'Created By',
						},
					],
					sortable: true,
					width: 150,
				},
				{
					id: 'created-date-column',
					fields: [
						{
							id: 'created-date-field',
							displayStyle: 'date',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'createdDate',
									objectTable: 'opportunity_note',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'muted',
								},
								formatOptions: {
									dateFormat: 'datetime-medium',
								},
							},
						},
					],
					headers: [
						{
							id: 'h-created',
							text: 'Created',
						},
					],
					sortable: true,
					width: 180,
				},
				{
					id: 'modified-by-column',
					fields: [
						{
							id: 'modified-by-field',
							displayStyle: 'text',
							dataConfig: {
								type: 'template',
								template: '{modifierFirstName} {modifierLastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
						},
					],
					headers: [
						{
							id: 'h-modified-by',
							text: 'Last Modified By',
						},
					],
					sortable: true,
					width: 150,
				},
				{
					id: 'modified-date-column',
					fields: [
						{
							id: 'modified-date-field',
							displayStyle: 'date',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'modifiedDate',
									objectTable: 'opportunity_note',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'muted',
								},
								formatOptions: {
									dateFormat: 'datetime-medium',
								},
							},
						},
					],
					headers: [
						{
							id: 'h-modified',
							text: 'Last Modified',
						},
					],
					sortable: true,
					width: 180,
				},
				{
					id: 'actions-column',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'editNote',
									// PD-17700: only the author, or a user with the "any" permission.
									visibilityCallback: 'canEditNote',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deleteNote',
									// PD-17700: only the author, or a user with the "any" permission.
									visibilityCallback: 'canDeleteNote',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [
						{
							id: 'h-actions',
							text: '',
						},
					],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			defaultSortId: 'last-modified-desc',
			sortOrder: 0,
		},
	],
	// Root-level tableSorts (TableSortOption name/fields) so defaultSortId
	// resolves — a list-level `sortFields` is not read by the resolver (PD-14536).
	tableSorts: [
		{
			id: 'last-modified-desc',
			name: 'Last modified (newest first)',
			fields: [
				{
					objectTable: 'opportunity_note',
					fieldName: 'modifiedDate',
					direction: 'DESC',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: [
			'noteCreatedDateRange',
			'noteCreatedByUser',
			'noteModifiedDateRange',
			'noteModifiedByUser',
		],
		defaultFilters: {},
	},
	objects: [
		{
			objectLabel: 'Opportunity Note',
			objectTable: 'opportunity_note',
			selectedFields: [
				{
					fieldName: 'id',
					fieldLabel: 'ID',
				},
				{
					fieldName: 'content',
					fieldLabel: 'Content',
				},
				{
					fieldName: 'isPinned',
					fieldLabel: 'Is Pinned',
				},
				{
					fieldName: 'createdDate',
					fieldLabel: 'Created Date',
				},
				{
					fieldName: 'modifiedDate',
					fieldLabel: 'Modified Date',
				},
			],
		},
		{
			objectLabel: 'Creator',
			objectTable: 'user',
			selectedFields: [
				{
					fieldName: 'creatorFirstName',
					fieldLabel: 'First Name',
				},
				{
					fieldName: 'creatorLastName',
					fieldLabel: 'Last Name',
				},
			],
		},
		{
			objectLabel: 'Modifier',
			objectTable: 'user',
			selectedFields: [
				{
					fieldName: 'modifierFirstName',
					fieldLabel: 'First Name',
				},
				{
					fieldName: 'modifierLastName',
					fieldLabel: 'Last Name',
				},
			],
		},
	],
};
